/**
 * Module dependencies.
 */

import {
  Copyright,
  Creator,
  FooterCompanySection,
  FooterContainer,
  FooterContent,
  FooterLink,
  FooterNewsletterSection,
  FooterQuickLinksSection,
  FooterQuickLinksTitle,
  Logo,
  LogoWrapper,
  Paragraph,
  QuickLink,
  QuickLinksList,
  SocialIcon,
  SocialIcons,
} from './footer.style';

import { Divider } from '@mui/material';
import {
  Facebook,
  Instagram,
  LinkedIn,
  Pinterest,
  Twitter,
  YouTube,
} from '@mui/icons-material';
import { JSX } from 'react';
import { Link } from 'components/atoms/link/link';
import { NewsletterForm } from '../newsletter-form/newsletter-form';
import { pageComponents } from 'constants/page-components';
import { useTranslation } from 'next-i18next';
import SvgLogo from 'assets/logo_white.svg';
import moment from 'moment';

/**
 * `Props` type.
 */

type Props = {
  socialNetworks: Record<string, string>;
};

/**
 * Export `Footer` component.
 */

export function Footer({ socialNetworks }: Props): JSX.Element {
  const [translate] = useTranslation();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterCompanySection>
          <LogoWrapper>
            <Logo as={SvgLogo} />
          </LogoWrapper>

          <Paragraph trim>{translate('footer.description')}</Paragraph>

          <SocialIcons>
            {socialNetworks.facebook?.trim()?.length > 0 && (
              <SocialIcon href={socialNetworks.facebook} target={'_blank'}>
                <Facebook />
              </SocialIcon>
            )}

            {socialNetworks.instagram?.trim()?.length > 0 && (
              <SocialIcon href={socialNetworks.instagram} target={'_blank'}>
                <Instagram />
              </SocialIcon>
            )}

            {socialNetworks.twitter?.trim()?.length > 0 && (
              <SocialIcon href={socialNetworks.twitter} target={'_blank'}>
                <Twitter />
              </SocialIcon>
            )}

            {socialNetworks.pinterest?.trim()?.length > 0 && (
              <SocialIcon href={socialNetworks.pinterest} target={'_blank'}>
                <Pinterest />
              </SocialIcon>
            )}

            {socialNetworks.linkedin?.trim()?.length > 0 && (
              <SocialIcon href={socialNetworks.linkedin} target={'_blank'}>
                <LinkedIn />
              </SocialIcon>
            )}

            {socialNetworks.youtube?.trim()?.length > 0 && (
              <SocialIcon href={socialNetworks.youtube} target={'_blank'}>
                <YouTube />
              </SocialIcon>
            )}
          </SocialIcons>
        </FooterCompanySection>

        <FooterQuickLinksSection>
          <FooterQuickLinksTitle>
            {translate('footer.quickLinks')}
          </FooterQuickLinksTitle>

          <QuickLinksList>
            {Object.keys(pageComponents).map((link) => (
              <QuickLink key={link}>
                <FooterLink href={pageComponents[link]}>
                  {translate(`links.${link}`)}
                </FooterLink>
              </QuickLink>
            ))}
          </QuickLinksList>
        </FooterQuickLinksSection>

        <FooterNewsletterSection>
          <NewsletterForm />
        </FooterNewsletterSection>
      </FooterContent>

      <Divider />

      <Copyright>
        <p>
          {translate('footer.copyright', { year: moment().format('YYYY') })}
        </p>
      </Copyright>

      <Link href={'https://www.franciscomorais.com'} target={'_blank'}>
        <Creator>{'Powered by Francisco Morais © 2025'}</Creator>
      </Link>
    </FooterContainer>
  );
}
